import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Logo from "../../Images/logo.png";
import { Api } from "../api";
import "./PrintReceipt.css";

const PrintReceipt = ({ sale, mode = "full" }) => {
  const { t, i18n } = useTranslation();
  const [containerEl, setContainerEl] = useState(null);
  const [fetchedRate, setFetchedRate] = useState(null);

  useEffect(() => {
    let el = document.getElementById("print-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "print-root";
      document.body.appendChild(el);
    }
    setContainerEl(el);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchRate = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await Api.get("/currencies/rate", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: { currencyId: 1 },
        });

        const data = res?.data;
        let rateVal = null;

        rateVal = data;

        if (isMounted && rateVal && !isNaN(rateVal) && rateVal > 0) {
          setFetchedRate(rateVal);
        }
      } catch (err) {
        console.error("Error fetching currency rate:", err);
      }
    };

    fetchRate();
    return () => {
      isMounted = false;
    };
  }, []);

  if (!sale || !containerEl) return null;

  const isFactory = mode === "factory";
  const isDouble = sale?.glass_type === "double";

  // Helper to extract item details
  const extractItems = () => {
    const items = [];
    let idx = 1;

    const currentLang = i18n.language || "ckb";

    (sale?.sale_details || []).forEach((detail) => {
      let mainName =
        detail?.item?.name?.[currentLang] ||
        detail?.item?.name?.ckb ||
        detail?.item?.name?.en ||
        detail?.item?.name?.ar ||
        detail?.customer_note ||
        "—";

      if (detail?.color?.name) {
        mainName += ` (${detail.color.name})`;
      }

      (detail?.sale_items || []).forEach((saleItem) => {
        let itemName = mainName;
        try {
          const services = JSON.parse(
            saleItem?.factory?.sale_item_service_ids || "[]"
          );
          if (Array.isArray(services) && services.length > 0) {
            const grouped = services.reduce((map, svc) => {
              const id = svc.sale_invoice_detail_id;
              if (!map[id]) map[id] = [];
              map[id].push(svc);
              return map;
            }, {});
            const groupedValues = Object.values(grouped);
            itemName = groupedValues
              .map((svcItems) => {
                const { item_name, color_name } = svcItems[0] || {};
                return `${item_name}${color_name ? `-${color_name}` : ""}`;
              })
              .join(" + ");
          }
        } catch (e) { }

        const height = parseFloat(saleItem?.height || 0);
        const width = parseFloat(saleItem?.width || 0);
        const quantity = parseInt(saleItem?.quantity || 0, 10);
        const meter = (width / 100) * (height / 100);
        const square =
          saleItem.square !== null &&
            saleItem.square !== "" &&
            saleItem.square !== undefined
            ? parseFloat(saleItem.square)
            : meter * quantity;

        const price = parseFloat(saleItem?.table_price || 0);

        let rowTotal = 0;
        if (isDouble) {
          rowTotal = square * price;
        } else {
          rowTotal = meter > 0 ? meter * quantity * price : quantity * price;
        }

        let doneQty = 0;
        if (saleItem?.factory) {
          if (Array.isArray(saleItem.factory) && saleItem.factory.length > 0) {
            doneQty = Number(
              saleItem.factory[saleItem.factory.length - 1]?.done_quantity || 0
            );
          } else if (typeof saleItem.factory === "object") {
            doneQty = Number(saleItem.factory?.done_quantity || 0);
          }
        } else if (
          Array.isArray(detail?.factories) &&
          detail.factories.length > 0
        ) {
          doneQty = Number(
            detail.factories[detail.factories.length - 1]?.done_quantity || 0
          );
        }

        items.push({
          index: idx++,
          itemName,
          height,
          width,
          sizeStr: `${height} × ${width}`,
          quantity,
          doneQty,
          square,
          price,
          rowTotal,
        });
      });
    });

    return items;
  };

  const items = extractItems();

  // Extract Notes
  const extractNotes = () => {
    const notes = [];
    (sale?.sale_details || []).forEach((detail, i) => {
      const noteText = isDouble ? detail?.extra_note : detail?.customer_note;
      if (noteText && noteText.trim()) {
        notes.push({
          index: i + 1,
          note: noteText,
        });
      }
    });
    return notes;
  };

  const notes = extractNotes();

  // Extract Images
  const extractImages = () => {
    const imgs = [];
    const baseUrl = process.env.REACT_APP_API_URL_IMAGE || "";
    (sale?.sale_details || []).forEach((detail) => {
      if (Array.isArray(detail?.images)) {
        detail.images.forEach((img) => {
          let src = "";
          if (typeof img === "string") {
            src = img.startsWith("http") ? img : `${baseUrl}${img}`;
          } else if (img && typeof img === "object") {
            const p = img.url || img.path || img.name || "";
            src = p.startsWith("http") ? p : `${baseUrl}${p}`;
          }
          if (src && !imgs.includes(src)) {
            imgs.push(src);
          }
        });
      }
    });
    return imgs;
  };

  const images = extractImages();

  // Customer Field Extraction
  const getCustomerName = (saleObj) => {
    if (!saleObj) return "—";
    if (saleObj.customer) {
      if (typeof saleObj.customer === "string") return saleObj.customer;
      if (Array.isArray(saleObj.customer) && saleObj.customer.length > 0) {
        const c = saleObj.customer[0];
        if (c?.name) return c.name;
        if (c?.user?.name) return c.user.name;
      }
      if (typeof saleObj.customer === "object") {
        if (saleObj.customer.name) return saleObj.customer.name;
        if (saleObj.customer.full_name) return saleObj.customer.full_name;
        if (saleObj.customer.username) return saleObj.customer.username;
        if (saleObj.customer.user?.name) return saleObj.customer.user.name;
      }
    }
    if (saleObj.customer_name) return saleObj.customer_name;
    if (saleObj.client_name) return saleObj.client_name;
    if (saleObj.user?.name) return saleObj.user.name;
    if (Array.isArray(saleObj.sale_details) && saleObj.sale_details.length > 0) {
      for (const d of saleObj.sale_details) {
        if (d?.customer?.name) return d.customer.name;
        if (d?.customer_name) return d.customer_name;
      }
    }
    if (saleObj.sale_invoice) {
      if (saleObj.sale_invoice.customer?.name) return saleObj.sale_invoice.customer.name;
      if (saleObj.sale_invoice.customer_name) return saleObj.sale_invoice.customer_name;
    }
    return "—";
  };

  const getCustomerPhone = (saleObj) => {
    if (!saleObj) return "—";
    if (saleObj.customer) {
      if (Array.isArray(saleObj.customer) && saleObj.customer.length > 0) {
        const c = saleObj.customer[0];
        if (c?.phone) return c.phone;
        if (c?.mobile) return c.mobile;
        if (c?.phone_number) return c.phone_number;
        if (c?.tel) return c.tel;
        if (c?.user?.phone) return c.user.phone;
      }
      if (typeof saleObj.customer === "object") {
        if (saleObj.customer.phone) return saleObj.customer.phone;
        if (saleObj.customer.mobile) return saleObj.customer.mobile;
        if (saleObj.customer.phone_number) return saleObj.customer.phone_number;
        if (saleObj.customer.tel) return saleObj.customer.tel;
        if (saleObj.customer.telephone) return saleObj.customer.telephone;
        if (saleObj.customer.user?.phone) return saleObj.customer.user.phone;
        if (saleObj.customer.user?.mobile) return saleObj.customer.user.mobile;
      }
    }
    if (saleObj.phone) return saleObj.phone;
    if (saleObj.mobile) return saleObj.mobile;
    if (saleObj.phone_number) return saleObj.phone_number;
    if (saleObj.customer_phone) return saleObj.customer_phone;
    if (saleObj.customer_mobile) return saleObj.customer_mobile;
    if (saleObj.user?.phone) return saleObj.user.phone;
    if (saleObj.user?.mobile) return saleObj.user.mobile;
    if (Array.isArray(saleObj.sale_details) && saleObj.sale_details.length > 0) {
      for (const d of saleObj.sale_details) {
        if (d?.customer?.phone) return d.customer.phone;
        if (d?.customer?.mobile) return d.customer.mobile;
        if (d?.customer_phone) return d.customer_phone;
        if (d?.phone) return d.phone;
      }
    }
    if (saleObj.sale_invoice) {
      if (saleObj.sale_invoice.customer?.phone) return saleObj.sale_invoice.customer.phone;
      if (saleObj.sale_invoice.customer?.mobile) return saleObj.sale_invoice.customer.mobile;
      if (saleObj.sale_invoice.phone) return saleObj.sale_invoice.phone;
    }
    return "—";
  };

  const getCustomerCode = (saleObj) => {
    if (!saleObj) return "—";
    if (saleObj.customer) {
      if (Array.isArray(saleObj.customer) && saleObj.customer.length > 0) {
        const c = saleObj.customer[0];
        if (c?.code) return c.code;
        if (c?.id) return c.id;
      }
      if (typeof saleObj.customer === "object") {
        if (saleObj.customer.code) return saleObj.customer.code;
        if (saleObj.customer.id) return saleObj.customer.id;
        if (saleObj.customer.customer_code) return saleObj.customer.customer_code;
      }
    }
    if (saleObj.customer_code) return saleObj.customer_code;
    if (saleObj.code) return saleObj.code;
    if (saleObj.user?.code) return saleObj.user.code;
    return "—";
  };

  const getCustomerAddress = (saleObj) => {
    if (!saleObj) return t("sulaymaniyah");
    if (saleObj.customer) {
      if (Array.isArray(saleObj.customer) && saleObj.customer.length > 0) {
        const c = saleObj.customer[0];
        if (c?.address) return c.address;
        if (c?.city) return c.city;
      }
      if (typeof saleObj.customer === "object") {
        if (saleObj.customer.address) return saleObj.customer.address;
        if (saleObj.customer.city) return saleObj.customer.city;
        if (saleObj.customer.location) return saleObj.customer.location;
      }
    }
    if (saleObj.address) return saleObj.address;
    if (saleObj.customer_address) return saleObj.customer_address;
    if (saleObj.location) return saleObj.location;
    return t("sulaymaniyah");
  };

  const getPaymentType = (saleObj) => {
    if (!saleObj) return t("cash");
    if (saleObj.payment_type) return saleObj.payment_type;
    if (saleObj.pay_type) return saleObj.pay_type;
    if (saleObj.type_payment) return saleObj.type_payment;
    if (saleObj.customer_payment_type) return saleObj.customer_payment_type;
    return saleObj.is_complete ? t("cash") : t("debt");
  };

  const customerName = getCustomerName(sale);
  const customerCode = getCustomerCode(sale);
  const customerPhone = getCustomerPhone(sale);
  const customerAddress = getCustomerAddress(sale);
  const paymentType = getPaymentType(sale);

  // Financial Summary Calculations
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalSquare = items.reduce((sum, item) => sum + item.square, 0);

  const subtotalItems = items.reduce((sum, item) => sum + (item.rowTotal || 0), 0);
  const expensesTotal = (sale?.expenses || []).reduce(
    (sum, exp) => sum + parseFloat(exp.amount || 0),
    0
  );

  const grossSubtotal = parseFloat(
    sale?.subtotal || sale?.sub_total || (subtotalItems + expensesTotal)
  );

  const discountAmount = parseFloat(
    sale?.discount || sale?.discount_amount || sale?.discount_value || 0
  );

  const netTotalUsd = parseFloat(
    sale?.total_usd ||
    sale?.total_after_discount ||
    (isDouble ? sale?.total_double : sale?.total) ||
    (grossSubtotal - discountAmount)
  );

  const normalizeRate = (rate) => {
    const r = parseFloat(rate);
    if (isNaN(r) || r <= 0) return null;
    if (r > 10000) return r / 100;
    if (r > 100) return r;
    return r;
  };

  const getRatePerDollar = (saleObj) => {
    if (fetchedRate) {
      const normalizedFetched = normalizeRate(fetchedRate);
      if (normalizedFetched) return normalizedFetched;
    }

    const saleRate =
      saleObj?.exchange_rate ||
      saleObj?.rate ||
      saleObj?.currency_rate ||
      saleObj?.iqd_rate ||
      saleObj?.currency?.rate ||
      saleObj?.currency?.price ||
      saleObj?.currency?.value;

    if (saleRate) {
      const normalizedSale = normalizeRate(saleRate);
      if (normalizedSale) return normalizedSale;
    }

    return 1500;
  };

  const ratePerDollar = getRatePerDollar(sale);
  const netTotalIqd = netTotalUsd * ratePerDollar;

  const currencySymbol = sale?.currency?.symbol || "$";
  const currentDir = i18n.dir ? i18n.dir() : (i18n.language === "en" ? "ltr" : "rtl");

  const content = (
    <div
      id="printable-receipt-document"
      className={`print-document ${isFactory ? "factory-view" : "full-view"}`}
      dir={currentDir}
    >
      <div className="print-document-main">
        {/* Official NERGIZ GLASS Header Banner */}
        <div className="print-header-banner">
          <div className="header-top-row justify-center">
            {/* Center: Logo */}
            <div className="header-logo-container">
              <img src={Logo} alt="NERGIZ GLASS" className="brand-logo" />
            </div>
          </div>

          {/* Yellow / Gold Accent Bar Under Header */}
          <div className="yellow-accent-bar"></div>
        </div>

        {/* Invoice Meta Grid with Red Highlights */}
        <div className="meta-info-box">
          <div className="meta-row">
            <div className="meta-col">
              <span className="label">{t("customer_name")}:</span>
              <span className="value text-red-highlight">{customerName}</span>
            </div>
            <div className="meta-col">
              <span className="label">{t("invoice")}:</span>
              <span className="value text-red-highlight">{sale?.invoice_number || "—"}</span>
            </div>
            <div className="meta-col">
              <span className="label">{t("sale_invoice_id")}:</span>
              <span className="value text-red-highlight">{sale?.id || "—"}</span>
            </div>
          </div>

          <div className="meta-row">
            {!isFactory && (
              <div className="meta-col">
                <span className="label">{t("customer_code")}:</span>
                <span className="value text-red-highlight">{customerCode}</span>
              </div>
            )}
            <div className="meta-col">
              <span className="label">{t("payment_type")}:</span>
              <span className="value text-red-highlight">{paymentType}</span>
            </div>
            <div className="meta-col">
              <span className="label">{t("phone")}:</span>
              <span className="value text-red-highlight dir-ltr">{customerPhone}</span>
            </div>
          </div>

          <div className="meta-row">
            <div className="meta-col">
              <span className="label">{t("address")}:</span>
              <span className="value text-red-highlight">{customerAddress}</span>
            </div>
            <div className="meta-col">
              <span className="label">{t("start_date")}:</span>
              <span className="value dir-ltr">
                {sale?.date ? sale.date.split(" ")[0] : "—"}
              </span>
            </div>
            <div className="meta-col">
              <span className="label">{t("terminate_date")}:</span>
              <span className="value dir-ltr">
                {sale?.terminate_date ? sale.terminate_date.split(" ")[0] : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Main Items Table */}
        <div className="print-table-wrapper">
          <h3 className="section-heading">
            {isFactory ? t("factory_work_details") : t("items_table")}
          </h3>
          <table className="receipt-table emerald-header">
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: isFactory ? "35%" : "25%" }}>{t("item_type_label")}</th>
                <th style={{ width: "20%" }}>{t("size_dimensions")}</th>
                <th style={{ width: "10%" }}>{t("quantity")}</th>
                {isFactory && <th style={{ width: "12%" }}>{t("done_quantity")}</th>}
                <th style={{ width: "15%" }}>{t("square_meter")}</th>
                {!isFactory && <th style={{ width: "15%" }}>{t("unit_price")}</th>}
                {!isFactory && <th style={{ width: "15%" }}>{t("total")}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="text-center">{item.index}</td>
                  <td className="text-start font-medium">{item.itemName}</td>
                  <td className="text-center">{item.sizeStr}</td>
                  <td className="text-center">{item.quantity}</td>
                  {isFactory && <td className="text-center">{item.doneQty}</td>}
                  <td className="text-center">{item.square.toFixed(3)} m²</td>
                  {!isFactory && (
                    <td className="text-center">
                      {item.price.toFixed(2)} {currencySymbol}
                    </td>
                  )}
                  {!isFactory && (
                    <td className="text-center font-bold">
                      {item.rowTotal.toFixed(2)} {currencySymbol}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="summary-row">
                <td colSpan={2} className="text-center font-bold">
                  {t("total")}
                </td>
                <td className="text-center">—</td>
                <td className="text-center font-bold">{totalQuantity} {t("unit_pieces")}</td>
                {isFactory && <td className="text-center">—</td>}
                <td className="text-center font-bold">
                  {totalSquare.toFixed(3)} m²
                </td>
                {!isFactory && <td colSpan={2}></td>}
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Comprehensive Financial Summary Box (SHOWN ONLY IN FULL PRINT) */}
        {!isFactory && (
          <div className="print-financial-summary price-section">
            <div className="financial-card">
              <div className="financial-row">
                <span className="financial-label">{t("subtotal_label")}:</span>
                <span className="financial-value">{grossSubtotal.toFixed(2)} $</span>
              </div>

              {discountAmount > 0 && (
                <div className="financial-row text-red-highlight">
                  <span className="financial-label">{t("discount_label")}:</span>
                  <span className="financial-value">{discountAmount.toFixed(2)} $</span>
                </div>
              )}

              <div className="financial-row highlight-usd">
                <span className="financial-label">{t("total_usd")}:</span>
                <span className="financial-value">{netTotalUsd.toFixed(2)} $</span>
              </div>

              <div className="financial-row highlight-iqd">
                <span className="financial-label">{t("total_iqd")}:</span>
                <span className="financial-value">
                  {Math.round(netTotalIqd).toLocaleString()} {t("currency_iqd")}
                </span>
              </div>

              <div className="financial-row">
                <span className="financial-label">{t("exchange_rate")}:</span>
                <span className="financial-value dir-ltr">
                  1 $ = {Math.round(ratePerDollar).toLocaleString()} {t("currency_iqd")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Notes Table */}
        {notes.length > 0 && (
          <div className="print-table-wrapper notes-section">
            <h3 className="section-heading">{t("notes")}</h3>
            <table className="receipt-table notes-table emerald-header">
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>#</th>
                  <th>{t("note")}</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((n, i) => (
                  <tr key={i}>
                    <td className="text-center">{n.index}</td>
                    <td className="text-start">{n.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Expenses Table (SHOWN ONLY IN FULL PRINT) */}
        {!isFactory && sale?.expenses?.length > 0 && (
          <div className="print-table-wrapper expenses-section price-section">
            <h3 className="section-heading">{t("expenses")}</h3>
            <table className="receipt-table emerald-header">
              <thead>
                <tr>
                  <th>{t("expense_note")}</th>
                  <th style={{ width: "25%" }}>{t("amount")}</th>
                </tr>
              </thead>
              <tbody>
                {sale.expenses.map((expense, ei) => (
                  <tr key={ei}>
                    <td className="text-start">{expense.note}</td>
                    <td className="text-center">
                      {expense.amount} {currencySymbol}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Attached Sketches / Images */}
        {images.length > 0 && (
          <div className="images-print-section">
            <h3 className="section-heading">
              {isFactory ? t("factory_attached_sketches") : t("images_and_handwritten_notes")}
            </h3>
            <div className="images-grid">
              {images.map((imgSrc, i) => (
                <div key={i} className="image-frame">
                  <img
                    src={imgSrc}
                    alt={`Sketch ${i + 1}`}
                    className="sketch-image"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, containerEl);
};

export default PrintReceipt;
